/**
 * Kopfbereich je Route — wörtlich aus `kopf()` des Entwurfs übernommen.
 *
 * Titel, Untertitel, Aktionen und Brotkrümelpfad. Routen ohne eigenen Eintrag
 * bekommen Titel und Untertitel aus der Navigationsregistry; erst wenn auch
 * dort nichts steht, greift „Nicht gefunden".
 */

import { NAVIGATION } from './navigation';

/**
 * Aktion: [Beschriftung, Art, optionales Ziel, optionale Untereinträge].
 *
 * `form:` öffnet den Drawer. Steht `eintraege`, wird die Schaltfläche zu
 * einem Aufklappmenü: der Kopf löst dann **den Untereintrag** als
 * Seitenaktion aus, nicht die Schaltfläche selbst. Die Seite meldet ihre
 * Handlungen also weiterhin unter den Namen der Einträge an — sie muss von
 * dem Menü nichts wissen.
 */
export type Kopfaktion = [
  label: string,
  art: 'prim' | 'sek',
  ziel?: string | undefined,
  eintraege?: string[] | undefined,
  /**
   * Das Recht, das diese Aktion braucht. Fehlt es, steht sie jedem offen.
   *
   * Gefiltert wird in `App.tsx` — nur dort steht `darfDas`, und die Rechte
   * kommen aus `/api/ich`. Ein Knopf ohne Recht **verschwindet**, er wird
   * nicht ausgegraut: die Anwendung zeigt keine Bedienelemente, die nichts
   * tun (Regel seit dem 13.08.2026).
   *
   * Gemeldet am 28.08.2026: eine Fuehrungskraft ohne `rollen.schreiben` sah
   * am Rollendetail „Bearbeiten" und landete im Editor vor einem Formular,
   * das sie nicht speichern kann.
   */
  recht?: string | null,
];

export interface Kopfbereich {
  titel: string;
  /** Kurzform für den Brotkrümelpfad. */
  kurz: string;
  beschreibung: string;
  aktionen: Kopfaktion[];
  breadcrumb: { label: string; href: string }[];
}

/**
 * Titel, Untertitel, Kopfaktionen und Bröselpfad je Route.
 *
 * **Ohne Zahlen.** Hier stand bis zum 28.08.2026: „Zahlen im Untertitel
 * stehen so im Entwurf; sie stammen aus dem Bestand und werden ersetzt,
 * sobald die Zählungen aus der API kommen." Ersetzt wurden sie nicht — sie
 * sind entfallen, am 12.08.2026 („Exchange 354") und am 27.08.2026 („Die 29
 * Bereiche", „345 Klärungsfragen"). Eine Zahl im Untertitel wird nie
 * nachgezählt: sie erscheint in Seitenleiste, Wegweiser, Suche, `/sitemap`
 * und im CSV-Export, und keine dieser Stellen fragt den Server.
 */
const K: Record<string, [string, string, Kopfaktion[], [string, string][]]> = {
  '/cockpit': ['Cockpit',
    'Überblick über Rollenbestand, Risiken und laufende Vorgänge',
    /* Zwei Ausgaben hinter **einer** Schaltflaeche. Der Kunde hat am
       25.08.2026 beide Formate verlangt — eine CSV mit allen Daten und einen
       HTML-Bericht zum Weitergeben — und daraus wurden zwei Knoepfe
       nebeneinander („Als CSV", „Als HTML"). Am 26.08.2026 hat er gesagt, wie
       er es haben will: oben rechts **ein** „Exportieren", und die Wahl des
       Formats dahinter.

       Das ist auch die bessere Anordnung. „Exportieren" ist die Handlung,
       CSV und HTML sind ihre Ausfuehrung; zwei gleichrangige Knoepfe zwingen
       zu einer Formatentscheidung, bevor ueberhaupt klar ist, dass es ums
       Exportieren geht. */
    [['Exportieren', 'sek', undefined, ['Als CSV', 'Als HTML']]], []],
  '/ausfuehrung': ['Ausführung',
    'Was ein Zielsystem noch nicht weiß — Aufträge an Runbooks',
    [], []],
  '/matrix': ['Rollenmatrix',
    'Eine Zeile je Berechtigung — von der Applikation über die Berechtigungsart bis zur technischen Gruppe',
    [['+ Neu', 'prim', 'form:berechtigung']], [['Rollen & Berechtigungen', '/matrix']]],
  '/rollen': ['Rollenkatalog',
    'Alle Standardrollen, gruppiert nach Organisationseinheit',
    /* „Big Picture" ist am 28.08.2026 entfallen — auf Wunsch des Kunden.
       Die Ansicht zeichnete eine Karte je Rolle; gebraucht wurde sie nicht,
       und ein Einstieg, den niemand nimmt, kostet trotzdem eine Entscheidung
       an jedem Seitenkopf. */
    [['+ Neu', 'prim', '/rollen/neu/bearbeiten']],
    [['Rollen & Berechtigungen', '/rollen']]],
  '/heatmap': ['Zugriffs-Heatmap',
    'Wo häufen sich Zugriffe? Rolle gegen Ressource, eine erklärte Skala, Drilldown rechts',
    [], [['Rollen & Berechtigungen', '/matrix']]],
  '/graph': ['Beziehungsgraph',
    'Rollen, Personen, Gruppen und Assets als Netz — Überblick und Fokus auf einen Knoten',
    [], [['Rollen & Berechtigungen', '/matrix']]],
  '/assets/applikationen': ['Applikationen & Services',
    'Anwendungen mit Owner, Lizenzmodell und Berechtigungskonzept',
    [], [['IT-Assets', '/assets/applikationen']]],
  '/assets/fileserver': ['Fileserver & Laufwerke',
    'Laufwerkzugriffe — Baum links, Berechtigungen des gewählten Pfades rechts',
    [], [['IT-Assets', '/assets/fileserver']]],
  '/assets/exchange': ['Exchange',
    'Funktionspostfächer und E-Mail-Verteiler',
    [], [['IT-Assets', '/assets/exchange']]],
  '/assets/m365': ['M365 & Teams',
    'Teams, M365-Gruppen und SharePoint-Sites mit Owner-, Gäste- und Aktivitätsprüfung',
    [], [['IT-Assets', '/assets/m365']]],
  '/assets/hardware': ['Hardware & Mobile',
    'Geräteklassen, Standardausstattung und freigegebene mobile Apps',
    [], [['IT-Assets', '/assets/hardware']]],
  '/assets/gruppen': ['Technische Gruppen',
    'Die Brücke zwischen Fachlichkeit und Technik — Entra- und AD-Gruppen mit Konventionsprüfung',
    [], [['IT-Assets', '/assets/gruppen']]],
  /* Ohne Kopfaktion: „+ Neu" hatte kein Ziel und keine angemeldete
     Seitenaktion und landete deshalb im Rueckfall von `Seitenkopf.tsx`
     („wird im Ausbau angebunden"). Die Seite selbst verweist daneben auf den
     Berichtsbaukasten — dorthin fuehrte der Knopf nicht einmal. */
  '/abfragen': ['Abfragen & Anomalien',
    'Auffälligkeiten im Bestand aufdecken — automatische Regeln, eigene Abfragen, Verlauf',
    [], [['Governance', '/abfragen']]],
  '/assets/lizenzen': ['Lizenzen & Kosten',
    'Was kostet der Rollenbestand? Auswertung, Rangfolge und Einsparpotenzial',
    [], [['IT-Assets', '/assets/lizenzen']]],
  '/organisation': ['Organisationseinheiten',
    'Die Bereiche mit Rollen, Personen, Assets und offenen Punkten',
    [], [['Organisation', '/organisation']]],
  '/personen': ['Personen',
    'Mitarbeitende, ihre Standardrolle und Zusatzberechtigungen',
    [['Vertretungen & Zuständigkeiten', 'sek', '/vertretungen']], [['Organisation', '/personen']]],
  /* Nicht mehr „mit Zeitleiste sowie Amtsträger mit Sonderrechten": die
     Zeitleiste zeichnete zwei Zahlen aus dem Quelltext, und die Amtsträger
     trugen Rollenkennungen, die es im Bestand nicht gibt. Beides ist am
     13.08.2026 entfallen. */
  '/vertretungen': ['Vertretungen & Zuständigkeiten',
    'Wer befristet vertritt und wer dauerhaft für ein Objekt geradesteht',
    [['Zu den Personen', 'sek', '/personen']], [['Organisation', '/personen']]],
  '/antraege/neu': ['Zugriff beantragen',
    'In vier Schritten: Empfänger, Positionen, Begründung mit Prüfung, Zusammenfassung',
    [], [['Anträge & Workflows', '/antraege/meine']]],
  '/antraege/meine': ['Meine Anträge',
    /* Nicht mehr „Genehmigungskette": `013` kennt keine benannten Stationen,
       sondern Positionen mit eigenem Zustand. Die Ansicht zeigt seit dem
       13.08.2026 „x von y entschieden"; eine Ueberschrift, die etwas anderes
       verspricht, waere der Rest einer geloeschten Erfindung. */
    'Zustand und Positionen der eigenen Anträge',
    [], [['Anträge & Workflows', '/antraege/meine']]],
  /* Runde 3, Punkt 7: der Bereich heisst Aenderungsfreigaben — Navigation
     und Kacheln waren umbenannt, dieser Kopf sagte noch den alten Namen. */
  '/genehmigungen': ['Änderungsfreigaben',
    'Vier-Augen-Prinzip: Änderungen an Rollen, Assets und Gruppen freigeben',
    [], [['Änderungsfreigaben', '/genehmigungen']]],
  /* Nicht mehr „drei geführte Prozesse": der Assistent mit seinen erfundenen
     Schritten ist am 13.08.2026 entfallen. `016` führt Vorgänge mit einer
     Aufgabenliste aus der Vorlage — die Überschrift sagt jetzt das. */
  '/lifecycle': ['Eintritt, Wechsel, Austritt',
    'Vorgänge mit Aufgabenliste — abgeschlossen wird erst, wenn keine Pflicht offen ist',
    [], [['Anträge & Workflows', '/lifecycle']]],
};

const SONDER: Record<string, [string, string]> = {
  '/profil': ['Mein Profil', 'Stammdaten, eigene Rollen, Benachrichtigungen und Sitzungen'],
  '/anmelden': ['Anmeldung', 'Zugang über das Geschäftskonto'],
};

/** Die Startseite trägt keinen Kopfbereich. */
export function kopfFuerRoute(pfad: string, suche = ''): Kopfbereich | null {
  if (pfad === '/') return null;

  if (pfad === '/rollen/bearbeiten') {
    /* Dieselbe Route bedient drei Faelle, und der Titel hat bis zum
       25.08.2026 in zweien davon gelogen: `?neu=1` legt an, `?vorlage=` legt
       eine Kopie an, ohne beides erscheint die Auswahl. „Rollen-Editor" stand
       ueber allen dreien. */
    const p = new URLSearchParams(suche);
    const vorlage = p.get('vorlage') ?? '';
    if (vorlage) {
      return {
        titel: 'Neue Rolle aus ' + vorlage,
        kurz: 'Kopie anlegen',
        beschreibung: 'Zuschnitt und Berechtigungen sind übernommen. Die Rollen-ID ist '
          + 'frei zu vergeben; der Name trägt vorerst „(Kopie)“.',
        aktionen: [['Vorlage ansehen', 'sek', '/rollen/' + encodeURIComponent(vorlage)]],
        breadcrumb: [{ label: 'Rollenkatalog', href: '/rollen' }],
      };
    }
    if (p.get('neu') === '1') {
      return {
        titel: 'Neue Rolle anlegen', kurz: 'Neue Rolle',
        beschreibung: 'Rollen-ID, Bezeichnung und Berechtigungen festlegen. Die ID wird aus '
          + 'Organisationseinheit, Abteilung und nächster freier Nummer vorgeschlagen.',
        aktionen: [], breadcrumb: [{ label: 'Rollenkatalog', href: '/rollen' }],
      };
    }
    return {
      titel: 'Rollen-Editor', kurz: 'Rollen-Editor',
      beschreibung: 'Stammdaten und Berechtigungen einer Standardrolle pflegen',
      aktionen: [], breadcrumb: [{ label: 'Rollen & Berechtigungen', href: '/rollen' }],
    };
  }

  /* Detailrouten haben keinen eigenen Eintrag: Titel und Brotkruemel ergeben
     sich aus dem Muster. Den fachlichen Titel — Name der Person, Bezeichnung
     der Rolle — setzt die Ansicht selbst, sobald der Datensatz da ist. */
  const editor = /^\/rollen\/(.+)\/bearbeiten$/.exec(pfad);
  if (editor) {
    const id = decodeURIComponent(editor[1] ?? '');
    return {
      titel: id === 'neu' ? 'Neue Rolle anlegen' : id + ' bearbeiten',
      kurz: id === 'neu' ? 'Neue Rolle' : 'Bearbeiten',
      beschreibung: id === 'neu'
        ? 'Rollen-ID, Bezeichnung und Berechtigungen festlegen. Die ID wird aus ' +
          'Organisationseinheit, Abteilung und nächster freier Nummer vorgeschlagen.'
        : 'Stammdaten und Berechtigungen dieser Standardrolle pflegen',
      aktionen: id === 'neu' ? [] : [['Vorschau', 'sek', '/rollen/' + id]],
      breadcrumb: [{ label: 'Rollenkatalog', href: '/rollen' }],
    };
  }

  const rolle = /^\/rollen\/([^/]+)$/.exec(pfad);
  if (rolle) {
    const id = decodeURIComponent(rolle[1] ?? '');
    return {
      titel: id, kurz: id,
      beschreibung: 'Standardrolle mit Berechtigungen, zugewiesenen Personen, SoD-Stand und Verlauf.',
      /* „Als Vorlage kopieren" fuehrt in den Editor mit gefuelltem Entwurf
         und leerer Kennung. Der Kunde hat es am 24.08.2026 gewuenscht: die
         meisten neuen Rollen unterscheiden sich von einer bestehenden um eine
         Handvoll Positionen, und die alle von Hand zu setzen ist die Arbeit,
         die niemand machen will. Der Weg dahinter gab es schon — die
         Vorlagenwahl im Zuordnungspanel; sie war nur von hier nicht
         erreichbar. */
      aktionen: [
        ['Bearbeiten', 'prim', '/rollen/' + id + '/bearbeiten', undefined,
          'rollen.schreiben'],
        /* Kopieren heisst **anlegen**, nicht pflegen — `048` trennt beides. */
        ['Als Vorlage kopieren', 'sek',
          '/rollen/bearbeiten?neu=1&vorlage=' + encodeURIComponent(id), undefined,
          'rollen.anlegen'],
        /* Die Berechtigungen dieser Rolle mitnehmen — als Tabelle oder als
           HTML-Uebersicht im Aufbau der Rollenansicht (30.09. und 06.10.2026,
           Kundenwunsch). Dasselbe Muster
           wie im Cockpit: eine Handlung, das Format dahinter. */
        ['Exportieren', 'sek', undefined, ['Als CSV', 'Als Übersicht']],
      ],
      breadcrumb: [{ label: 'Rollenkatalog', href: '/rollen' }],
    };
  }

  const person = /^\/personen\/([^/]+)$/.exec(pfad);
  if (person) {
    const id = decodeURIComponent(person[1] ?? '');
    return {
      titel: id, kurz: id,
      /* Ohne „Anträge und Prüfstand": beide Reiter sind am 27.08.2026
         entfallen, weil sich fuer keinen von beiden eine Quelle abfragen
         laesst. Herleitung im Kopf von `seiten/Personendetail.tsx`. */
      beschreibung: 'Was diese Person wirklich hat — Zugriffe mit Herkunft, Zustand und Rezertifizierungsstand.',
      aktionen: [], breadcrumb: [{ label: 'Personen', href: '/personen' }],
    };
  }

  const oe = /^\/organisation\/([^/]+)$/.exec(pfad);
  if (oe) {
    const code = decodeURIComponent(oe[1] ?? '');
    return {
      titel: code, kurz: code,
      beschreibung: 'Rollen, Personen, Assets und offene Punkte dieser Organisationseinheit.',
      aktionen: [], breadcrumb: [{ label: 'Organisationseinheiten', href: '/organisation' }],
    };
  }

  const fest = K[pfad];
  if (fest) {
    return {
      titel: fest[0], kurz: fest[0], beschreibung: fest[1], aktionen: fest[2],
      breadcrumb: fest[3].map(([label, href]) => ({ label, href })),
    };
  }

  // Geplante Route: Titel und Untertitel kommen aus der Navigationsregistry
  const eintrag = NAVIGATION.flatMap((g) => g.eintraege).find((it) => it.route === pfad);
  if (eintrag) {
    return {
      titel: eintrag.titel, kurz: eintrag.titel, beschreibung: eintrag.beschreibung,
      aktionen: [], breadcrumb: [],
    };
  }

  const sonder = SONDER[pfad];
  if (sonder) {
    return { titel: sonder[0], kurz: sonder[0], beschreibung: sonder[1], aktionen: [], breadcrumb: [] };
  }

  return { titel: 'Nicht gefunden', kurz: '404', beschreibung: '', aktionen: [], breadcrumb: [] };
}
